/*
TRANE CONFIDENTIAL

2021 – 2022 Copyright Trane U.S. Inc.
All Rights Reserved.

NOTICE: All information contained herein is, and remains the property of
Trane U.S. Inc. The source code, object code, intellectual and technical
concepts contained herein are proprietary to Trane U.S. Inc. and may be
covered by patents and patent applications and are protected by trade secret
and copyright law. Dissemination of this information or reproduction of this
material is strictly forbidden unless prior written permission is obtained
from Trane U.S. Inc. Do not use this software without the prior written
permission of Trane U.S. Inc.
*/

/* @flow */
import * as React from "react";
import debounce from "lodash/debounce";
import { withColorsProvider, type ColorSet } from "../../ColorsProvider";
import Theme from "../../Theme";
import { Divider } from "../../ItemList";
import prefixStyles from "../../utils/prefixStyles";
import FormError from "../../FormError";
import InfoIcon from "./FormFieldIcons/InfoIcon";
import RevealPasswordIcon from "./FormFieldIcons/RevealPasswordIcon";
import { IconPencil } from "../../Icons";

const FORM_FIELD_HEIGHT = "48px";

type Props = {
  alignInputLeft?: ?boolean,
  autoFocus?: ?boolean,
  captureRef?: ?(?HTMLInputElement) => void,
  error?: ?string,
  hasLeftLabel?: ?boolean,
  infoIcon?: ?boolean,
  infoText?: ?string,
  label?: ?string,
  max?: ?number,
  maxLength?: ?number,
  min?: ?number,
  name: string,
  onEnterPressed?: ?() => void,
  onChange?: ?(value: string | number) => void,
  shouldScrollIntoView?: ?boolean,
  type: "date" | "email" | "number" | "password" | "text" | "time" | "tel",
  value: string | number,
  colors: ColorSet,
};

type State = {
  type: $PropertyType<Props, "type"> | "",
};

class InputField extends React.Component<Props, State> {
  static defaultProps = {
    value: "",
  };

  state = {
    type: this.props.type || "",
  };

  _input: ?HTMLInputElement;

  _captureRef = (el) => {
    this._input = el;

    if (this.props.captureRef) {
      this.props.captureRef(el);
    }
  };

  _handleKeyUp = (event) => {
    let enterKeycode = 13;
    if (event.keyCode === enterKeycode) {
      if (this._input) {
        this._input.blur();
      }
      if (this.props.onEnterPressed) {
        this.props.onEnterPressed();
      }
    }
  };

  _scrollToField: ?() => void;

  _handleFocus = () => {
    this._debounceFocus();
    if (this.props.shouldScrollIntoView) {
      // Android resizes the window AFTER the keybaord appears, so sometimes scrolling doesn't work
      // https://stackoverflow.com/questions/23757345/android-does-not-correctly-scroll-on-input-focus-if-not-body-element
      if (this._scrollToField) {
        window.removeEventListener("resize", this._scrollToField);
      }
      this._scrollToField = () => {
        window.setTimeout(() => {
          if (!this._input || document.activeElement !== this._input) {
            return;
          }
          // scrollIntoView is largely supported but the options are not.
          // This will try to center the field or will scroll to the top by default.
          // $FlowIgnore
          this._input.scrollIntoView({ block: "center" });
        }, 0);
      };
      window.addEventListener("resize", this._scrollToField);
    }
  };

  _debouncedFocused: boolean;

  _debounceFocus = debounce(() => {
    this._debouncedFocused = true;
    // The 100ms here is racing the amount of time it takes the mobile
    // browser to process an event which changes focus (eg click).
  }, 100);

  _handleBlur = () => {
    this._debounceBlur();
    window.removeEventListener("resize", this._scrollToField);
    this._scrollToField = null;
  };

  _debounceBlur = debounce(() => {
    this._debouncedFocused = false;
    // The 100ms here is racing the amount of time it takes the mobile
    // browser to process an event which changes focus (eg click).
  }, 100);

  _handleRevealPassword = () => {
    const { type } = this.state;

    this.setState({ type: type === "password" ? "text" : "password" }, () => {
      if (this._debouncedFocused && this._input) {
        // In the situation where the input field is focused and you tap on
        // the reveal password icon, the software keyboard hides, because
        // the password icon is the active element now, instead of the input.
        // This is annoying, so re-focus the input so that the software keyboard
        // remains open.
        this._input.focus();
      }
    });
  };

  componentDidMount() {
    if (this.props.autoFocus) {
      if (this._input) {
        this._input.focus();
      }
    }
  }

  render() {
    const {
      alignInputLeft,
      error,
      hasLeftLabel,
      infoIcon,
      infoText,
      label,
      max,
      maxLength,
      min,
      name,
      onChange,
      type: pType,
      value,
      colors,
    } = this.props;

    const styles = prefixStyles({
      wrapper: {
        marginLeft: "0",
        marginRight: "0",
        position: "relative",
        background: "white",
        color: colors.TEXT,
      },
      fieldWrapper: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        boxSizing: "border-box",
        backgroundColor: "white",
      },
      label: {
        flex: "0 0 auto",
        paddingLeft: Theme.Padding.CONTENT,
        paddingTop: Theme.Padding.CONTENT,
        paddingBottom: Theme.Padding.CONTENT,
        display: "flex",
        alignItems: "center",
        maxWidth: "100%", // Allows long labels to word-wrap
      },
      floatingIcons: {
        // These are floating on top of the input instead of beside it
        // because we want them to appear "inside" the input when Chrome
        // autocomplete colors the input yellow, or when the input field is
        // focused by a screen reader and the bounds rectangle is visible, etc.
        position: "absolute",
        right: "0",
        bottom: "0",
        // Matching height here instead of using top: 0 because when a form error
        // appears, we only want to float the icons over the input, not the error
        // part.
        height: FORM_FIELD_HEIGHT,
        display: "inline-flex",
        alignItems: "center",
        paddingRight: Theme.Padding.CONTENT,
      },
    });

    let revealPassword = false;

    const { type } = this.state;

    if (pType === "password") {
      revealPassword = true;
    }

    let hasClickableIcons = infoIcon || revealPassword;
    let hasIcons = hasClickableIcons || hasLeftLabel;
    let numIcons = [infoIcon, revealPassword, hasLeftLabel].filter(Boolean)
      .length;

    const formFieldStyle = {
      WebkitAppearance: "none",
      // the apple system font has ridiculous password dots. Users shouldn't notice the flash of sans-serif isn't technically San Francisco
      fontFamily: type === "password" ? "sans-serif" : "inherit",
      fontWeight: "inherit",
      color: "inherit",
      backgroundColor: "white",
      border: "none",
      borderRadius: "0",
      margin: "0",
      paddingRight: Theme.Padding.CONTENT + numIcons * 25, // 25 extra px per icon
      paddingLeft: Theme.Padding.CONTENT,
      fontSize: "1em",
      width: "100%",
      minWidth: "12em",
      minHeight: `calc(2 * ${Theme.Padding.CONTENT}px + 1.2em)`, // 1.2em is roughly line-height: normal. This prevents fields with empty values from collapsing
      height: FORM_FIELD_HEIGHT,
      flex: "1 1 10em",
      textOverflow: "ellipsis",
      textAlign: alignInputLeft || !hasLeftLabel ? "" : "right",
    };

    const iconSpacing = <div style={{ paddingRight: "10px" }} />;

    return (
      <div style={styles.wrapper}>
        <FormError error={error}>
          <div style={styles.fieldWrapper}>
            {hasLeftLabel ? (
              <label htmlFor={name} style={styles.label}>
                {label}
              </label>
            ) : null}
            <input
              ref={this._captureRef}
              type={type}
              name={name}
              style={formFieldStyle}
              placeholder={hasLeftLabel ? null : label}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              maxLength={maxLength}
              min={min}
              max={max}
              pattern={type === "number" ? "[0-9]*" : undefined}
              value={value}
              onChange={onChange}
              onKeyUp={this._handleKeyUp}
              onFocus={this._handleFocus}
              onBlur={this._handleBlur}
            />
          </div>
          {hasIcons ? (
            <div
              style={{
                ...styles.floatingIcons,
                pointerEvents: hasClickableIcons ? "" : "none",
              }}
            >
              {hasLeftLabel ? <IconPencil /> : null}
              {revealPassword || infoIcon ? iconSpacing : null}
              {revealPassword ? (
                <RevealPasswordIcon
                  type={type}
                  onClick={this._handleRevealPassword}
                />
              ) : null}
              {revealPassword && infoIcon ? iconSpacing : null}
              {infoIcon ? <InfoIcon dialogText={infoText} /> : null}
            </div>
          ) : null}
          <Divider />
        </FormError>
      </div>
    );
  }
}

export default withColorsProvider(InputField);



// WEBPACK FOOTER //
// ../nx-components/src/Form/fields/InputField.jsx